import React, { useState } from 'react';
import { uniqueId } from 'lodash';
import {
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Icon,
  Radio,
  RadioGroup,
  RadioProps,
  Stack,
  // TextField,
  Typography,
} from '@mui/material';
import { green } from '@mui/material/colors';
import CustomAccordion from '../../../common/CustomAccordion';
import { formatValue } from '../utils';
import MemoizedField from '../../../../pages/edit/components/MemoizedField';
import { RadioGroupTypes } from '../utils/types';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface RadioDataInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  'data-record-id'?: string;
}

interface CustomRadioProps extends RadioProps {
  dataInputProps?: RadioDataInputProps;
}

const LARadioOption = ({ value, control, label }: any) => {
  return (
    <Stack direction="row" spacing={0} useFlexGap alignItems="center">
      <FormControlLabel value={value} control={control} label={label} />
      <Chip
        label="AI"
        color="primary"
        size="small"
        sx={{
          height: 18,
          background: 'primary.main',
          '& .MuiChip-label': {
            px: 1.5,
            fontSize: 12,
          },
        }}
      />
    </Stack>
  );
};

const AIRadioOption = ({ value, control, label }: any) => {
  return (
    <Stack direction="row" spacing={0} useFlexGap alignItems="center">
      <FormControlLabel value={value} control={control} label={label} />
      <AutoAwesomeIcon color={'primary'} />
    </Stack>
  );
};

const SvgIcon = ({ width = 20, sx = {} }) => (
  <Icon sx={{ width, height: width, m: 0, fontSize: 1, ...sx }}>
    <img
      alt=""
      src="/icons/check_circle.svg"
      width={width - 2}
      height={width - 2}
    />
  </Icon>
);

const RadioControl = ({ dataInputProps, ...props }: CustomRadioProps) => {
  return (
    <Radio
      size="small"
      inputProps={{
        ...dataInputProps,
      }}
      sx={{
        p: 1,
        '&.Mui-checked': {
          color: green[600],
        },
      }}
      checkedIcon={<SvgIcon />}
      {...props}
    />
  );
};

const getValue = (value: any) =>
  Array.isArray(value) ? value.join(', ') : value;

const AccordionRadioButton = React.memo(
  ({
    field,
    options,
    isAI: isListingAssistantBestGuess,
    defaultExpanded,
    onClick,
  }: any) => {
    const name = field?.metadata?.Name || '';
    const defaultValue =
      isListingAssistantBestGuess && options?.length
        ? getValue(options[0].value)
        : null;
    const resolvedValue = field.resolvedData?.value || null;
    const [selectedValue, setSelectedValue] = useState<any>(
      resolvedValue || field.listingContextValue || defaultValue,
    );
    const showSavedIcon =
      field.listingContextValue || field.listingContextValue === 0;
    const title = (
      <FormLabel sx={{ color: 'inherit' }}>
        <Typography variant="subtitle1" fontWeight="fontWeightBold">
          {/* {field.metadata.Name} */}
          {name}
          {'     '}
          {showSavedIcon ? (
            <SvgIcon width={16} sx={{ verticalAlign: 'middle' }} />
          ) : (
            ''
          )}
        </Typography>
      </FormLabel>
    );

    const onChange = (
      e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const { currentTarget: target } = e;
      const { recordId = '' } = target.dataset || {};
      e?.currentTarget && setSelectedValue(e.currentTarget.value);
      onClick(e, {
        recordId,
        value: e.currentTarget.value,
      });
    };

    return (
      <FormControl sx={{ width: '100%' }}>
        <CustomAccordion
          title={title}
          defaultExpanded={!!defaultExpanded}
          variant="small"
        >
          <RadioGroup value={selectedValue} onChange={(e) => onChange(e)}>
            {options?.length
              ? options.map((option: any) => {
                  const value = getValue(option.value);
                  const isAIOption = option.recordType === 'AI';
                  const formattedValue = formatValue(
                    value,
                    field.metadata?.Control || field.controlField?.Control,
                  );
                  const inputProps = {
                    'data-record-id': option.recordId,
                  };
                  const radioProps = {
                    value,
                    control: <RadioControl dataInputProps={inputProps} />,
                    label: isAIOption
                      ? `AI Recommendation: ${formattedValue}`
                      : `${option.label}: ${formattedValue}`,
                  };
                  const valueKey = uniqueId(value);
                  return isListingAssistantBestGuess ? (
                    <LARadioOption key={valueKey} {...radioProps} />
                  ) : isAIOption ? (
                    <AIRadioOption key={valueKey} {...radioProps} />
                  ) : (
                    <FormControlLabel key={valueKey} {...radioProps} />
                  );
                })
              : null}
            <FormControlLabel
              value={RadioGroupTypes.CUSTOM}
              control={
                <RadioControl
                  dataInputProps={{
                    'data-record-id': RadioGroupTypes.CUSTOM,
                  }}
                />
              }
              label="Custom value"
            />
            {selectedValue === RadioGroupTypes.CUSTOM && (
              <MemoizedField
                field={field.controlField || field.metadata}
                onCustomControlLoaded={() => {}}
                showLabel={false}
              />
            )}
            <FormControlLabel
              value={RadioGroupTypes.NONE}
              control={
                <RadioControl
                  dataInputProps={{
                    'data-record-id': RadioGroupTypes.NONE,
                  }}
                />
              }
              label="Proceed with None"
            />
          </RadioGroup>
        </CustomAccordion>
      </FormControl>
    );
  },
  (prevProps, nextProps) =>
    prevProps.defaultExpanded === nextProps.defaultExpanded &&
    prevProps.field === nextProps.field &&
    prevProps.isAI === nextProps.isAI &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.options.length === nextProps.options.length,
);

export default AccordionRadioButton;
